import React, { useEffect, useMemo, useState } from "react";
import ReusableTable from "../../components/Employee/Table";
import "./OvertimeListLayout.css";
import { Navigate, useNavigate } from "react-router-dom";
import { useGetAllOvertimeRequestsPaginated } from "../../hooks/useOvertime";
import { formatDateOnly, getMediaBase64 } from "../../util/common";

export default function OvertimeListLayout() {
  const [pageCount, setPageCount] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
  });

  const [filter, setFilter] = useState({
    ApprovalStatus: null,
    OrderBy: "desc",
  });

  const overtimeRequests = useGetAllOvertimeRequestsPaginated();
  const [overtimeRecords, setOvertimeRecords] = useState([]);

  const fetchOvertimeRecords = () => {
    overtimeRequests.mutate(
      {
        page: pagination.page,
        pageSize: pagination.pageSize,
        filters: filter,
      },
      {
        onSuccess: async (result) => {
          if (!result.success) return;

          const records = await Promise.all(
            result.data.data.map(async (record) => {
              return {
                ShiftDate: formatDateOnly(record.ShiftDate),
                ShiftTimeFrom: record.ShiftTimeFrom,
                ShiftTimeTo: record.ShiftTimeTo,
                OTType: record.OTType,
                OTStart: record.OTStart,
                OTEnd: record.OTEnd,
                Reason: record.Reason,
                Media: await getMediaBase64(record?.Media?.MediaUrl),
                Approver: record.Approver?.FullName,
                ApprovedAt: formatDateOnly(record.ApprovedAt),
                ApprovalStatus: record.ApprovalStatus,
              };
            })
          );
          setOvertimeRecords(records);
          setPageCount(result.data.pagination.totalPages);
        },
      }
    );
  };

  const handleOnPageChange = (pageIndex, pageSize) => {
    setPagination({
      page: pageIndex + 1,
      pageSize,
    });
  };

  useEffect(() => {
    fetchOvertimeRecords();
  }, [pagination, filter]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "ShiftDate",
        header: "Shift Date",
      },
      {
        accessorKey: "ShiftTimeFrom",
        header: "Shift Time From",
      },
      {
        accessorKey: "ShiftTimeTo",
        header: "Shift Time To",
      },
      {
        accessorKey: "OTType",
        header: "OT Type",
      },
      {
        accessorKey: "OTStart",
        header: "OT Start",
      },
      {
        accessorKey: "OTEnd",
        header: "OT End",
      },
      {
        accessorKey: "Reason",
        header: "Reason",
      },
      {
        accessorKey: "Media",
        header: "Media",
        cell: ({ getValue }) => {
          const url = getValue();
          if (!url) return "No Image";
          return (
            <img
              src={url}
              alt="Media"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
                borderRadius: "6px",
              }}
            />
          );
        },
      },
      {
        accessorKey: "Approver",
        header: "Approver Name",
      },
      {
        accessorKey: "ApprovedAt",
        header: "Approved At",
      },
      {
        accessorKey: "ApprovalStatus",
        header: "Status",
        cell: ({ row }) => {
          const status = row.original.ApprovalStatus;
          return (
            <span className={`status-badge status-${status.toLowerCase()}`}>
              {status}
            </span>
          );
        },
      },
    ],
    []
  );

  const navigate = useNavigate();

  return (
    <div className="overtime-list-container">
      <div className="header-row">
        <h3>Overtime Records</h3>

        <div className="filters-right">
          <button className="overtime-btn" onClick={() => navigate("/OvertimeApplication")}>
            + Overtime
          </button>

          <select
            value={filter.ApprovalStatus}
            className="status-filter"
            onChange={(e) => {
              setFilter({
                ApprovalStatus: e.target.value,
                OrderBy: filter.OrderBy,
              });
            }}
          >
            <option value="">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      <ReusableTable
        data={overtimeRecords}
        columns={columns}
        mode="server"
        initialPageSize={pagination.pageSize}
        onPageChange={handleOnPageChange}
        pageCount={pageCount}
        page={pagination.page - 1}
        pageSize={pagination.pageSize}
      />
    </div>
  );
}
